import { User } from '../profile/types';
import { Shift } from '../resourceTracking/types';

/**
 * Per-location SharePoint documentation-link configuration. All five fields are tightly coupled —
 * they're either all populated (resolver builds SharePoint URLs for job stations in this location)
 * or all null (resolver returns no link unless a per-station override is set). The admin UI enforces
 * this all-or-nothing rule; see {@link InventoryCenterSharepointConfigInput}.
 *
 * Persisted server-side as an optional (0..1) side table, so on the wire this is `null` when the
 * location has no configuration rather than an object of all-null fields.
 */
export type InventoryCenterDocumentationConfig = {
  /** SharePoint document library (Graph list name). */
  sharepointDocumentationLibrary: string | null;
  /** Display name of the SharePoint column holding the work-cell (MCell) value. */
  workCellColumnDisplayName: string | null;
  /** Internal Graph field name matching {@link workCellColumnDisplayName}. */
  workCellColumnInternalName: string | null;
  /** Display name of the SharePoint column holding the job-station title. */
  jobStationColumnDisplayName: string | null;
  /** Internal Graph field name matching {@link jobStationColumnDisplayName}. */
  jobStationColumnInternalName: string | null;
};

export type InventoryCenter = {
  id: string;
  code: string;
  description: string;
  displayName: string | null;
  enabled: boolean;
  /**
   * Optional SharePoint documentation-link configuration for this location, or `null` when the
   * location has no configuration.
   */
  documentationConfig: InventoryCenterDocumentationConfig | null;
};

export type InventoryCenterConfigOptions = Pick<
  InventoryCenter,
  'displayName' | 'enabled'
>;

/**
 * Payload sent to `PATCH /api/ManufacturingLocation/inventoryCenter/{id}/sharepoint-config`.
 *
 * `Required<>` strips the `?` modifier and `undefined` from the entity definition so the wire
 * shape is unambiguous: every key must be present, and a value of `null` explicitly clears
 * that field. The admin dialog enforces that either all five fields are populated together
 * or all five are cleared together; the backend tolerates partial updates but the UI never
 * sends them.
 *
 * Kept separate from {@link InventoryCenterConfigOptions} because the two map to different
 * backend endpoints, have different authorization policies, and have different invariants
 * (the SharePoint fields form an all-or-nothing group; displayName/enabled are independent).
 */
export type InventoryCenterSharepointConfigInput =
  InventoryCenterDocumentationConfig;

export type PlanningDepartment = {
  id: string;
  code: string;
  description: string;
  isDepartmentOfInterest: boolean;
  inventoryCenter?: InventoryCenter;
};

export type MCell = {
  id: string;
  code: string;
  description: string;
  inventoryCenter: InventoryCenter;
  inventoryCenterId: string;
  isRollup: boolean;
  componentMCells: Array<MCellRollupComponentJoin>;
  sortSequence: number;
  enabled: boolean;
  isLocal: boolean;
  productionType: ProductionType;
  /** Set when the cell was created by the clone feature; the source cell's id */
  clonedFromMCellId?: string | null;
  /** A cell whose badge logins also count toward this cell's experience (set by the clone feature) */
  experienceSourceMCellId?: string | null;
};

export const productionTypes = [
  'Unknown',
  'Standard',
  'Warehouse',
  'LoadingDock',
] as const;

export type ProductionType = (typeof productionTypes)[number];

export type MCellFilter = Partial<ManufacturingLocation> & {
  includeRollup: boolean;
  includeComponentMCells: boolean;
};

export type MCellRollupComponentJoin = {
  rollupId: string;
  componentId: string;
  component: MCell;
};

export type MCellInfo = {
  code: string;
  description: string;
  inventoryCenterId: string;
  sortSequence: number;
  enabled: boolean;
  productionType: ProductionType;
};

export type MCellDto = {
  id: string;
  code: string;
  description: string;
  inventoryCenterId: string;
  isRollup: boolean;
};

/**
 * Aspects of a work cell that the clone feature can copy, join or move. Mirrors the backend MCellCloneAspect enum.
 */
export type MCellCloneAspect =
  | 'CompletionConfigs'
  | 'JobStations'
  | 'CrewingThresholds'
  | 'CellBoardConfigs'
  | 'SafetyMessages'
  | 'Schedules'
  | 'CollectionMemberships'
  | 'SupportHierarchyMemberships'
  | 'HomeLinks'
  | 'HomeEmployees'
  | 'Assets'
  | 'Experience';

export type MCellCloneAspectCount = {
  aspect: MCellCloneAspect;
  count: number;
};

export type MCellClonePreview = {
  sourceMCellId: string;
  suggestedCode: string;
  aspectCounts: Array<MCellCloneAspectCount>;
};

export type MCellCloneCreate = {
  sourceMCellId: string;
  code: string;
  description: string;
  sortSequence: number;
  enabled: boolean;
  productionType: ProductionType;
  aspects: Array<MCellCloneAspect>;
};

export type MCellCloneAspectResult = {
  aspect: MCellCloneAspect;
  /** Number of items copied, joined or moved for this aspect */
  count: number;
};

export type MCellCloneResult = {
  mCell: MCell;
  aspectResults: Array<MCellCloneAspectResult>;
};

export type MCellCollectionMCell = {
  mCellCollectionId: string;
  mCellId: string;
  mCell: MCell;
};

export type MCellCollection = {
  id: string;
  name: string;
  createdBy: User;
  mCellCollectionMCell: Array<MCellCollectionMCell>;
  isGlobal: boolean;
};

export type MCellCollectionCreateType = {
  name: string;
  mCellIds: Array<string>;
};

export type ManufacturingLocation = {
  mCellId: string;
  inventoryCenterId: string;
  mCellCollectionId: string;
};

export type WorkCenterProductionLineManufacturingLocation = {
  mCellId: string;
  inventoryCenterId: string;
  workCenterProductionLineId: string;
};

export type PlanningDepartmentOfInterestUpdate = {
  id: string;
  isDepartmentOfInterest: boolean;
};

export type KeyMCell = {
  mCell: MCell;
  mCellId: string;
  isKey: boolean;
};

export type KeyMCellUpdate = Omit<KeyMCell, 'mCell'>;

export const ticketSystems = ['Email'] as const;
export type TicketSystem = (typeof ticketSystems)[number];

export type ManufacturingLocationNotificationSystem = {
  id: string;
  inventoryCenter?: InventoryCenter;
  inventoryCenterId: string;
  ticketSystem: TicketSystem;
};

export type ManufacturingLocationNotificationSystemInfo = {
  inventoryCenterId: string;
  ticketSystem: TicketSystem;
};

export type ManufacturingLocationTimeRangeShiftFilter = {
  mCellId?: string;
  inventoryCenterId?: string;
  mCellCollectionId?: string;
  shifts?: Shift[];
  startDate?: Date | null;
  endDate?: Date | null;
};

export type ProducingLocation = {
  id: string;
  icimKey: string;
  code: string;
  description: string;
  planningDepartment: PlanningDepartment;
  planningDepartmentId: string;
};

export type MCellCompletionConfigDto = {
  id: string;
  producingLocationId?: string;
  producingLocationCode?: string;
  prodLineId?: string;
  prodLineCode?: string;
  workCenterId?: string;
  workCenterCode?: string;
  workCenterStationId?: string;
  workCenterStationName?: string;
};

export type MCellCompletionConfigCreate = {
  mCellId: string;
  producingLocationId: string;
  productionLineId?: string;
  workCenterId?: string;
  workCenterStationId?: string;
};

export type MCellCompletionConfigUpdate = {
  producingLocationId: string;
  productionLineId?: string;
  workCenterId?: string;
  workCenterStationId?: string;
};
